import React, { ReactNode } from 'react';
import Card from '@components/framework/card/Card.tsx';
import EditableHeading3 from '@components/ringil3/Heading/EditableHeading3.tsx';
import SvgMapPin from '@icons/map-pin.svg?react';
import SvgCalendar from '@icons/calendar.svg?react';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import { SecuredConfig } from '@components/security/securedDecoration';

interface DateSection {
  label: string;
  // pencil shows when editable or secured rules allow it; opens the consumer's own sidebar
  onEdit?: () => void;
  editable?: boolean;
  secured?: SecuredConfig;
  content?: ReactNode;
  // rendered when content is absent (e.g. COInlineMessage "not set" + CTA)
  emptyState?: ReactNode;
}

interface LocationDetailCardProps {
  title: string;
  // header pencil → consumer's entity-specific edit sidebar
  onEdit?: () => void;
  editable?: boolean;
  editSecured?: SecuredConfig;

  companyName?: string;
  // formatter-agnostic: parent passes the formatted address node
  address?: ReactNode;
  timezoneId?: string;

  // all slots arrive pre-decorated for security from the parent
  referenceSlot?: ReactNode;
  badgesSlot?: ReactNode;
  contactsSlot?: ReactNode;
  noteSlot?: ReactNode;

  showDivider?: boolean;
  dateSection?: DateSection;
  // facility ref (e.g. reservation ramp) — distinct from contacts
  rampName?: ReactNode;

  dataTestId?: string;
}

const textLine = 'text-[15px] leading-[normal] text-content-primary';

const LocationDetailCard: React.FC<LocationDetailCardProps> = ({
  title,
  onEdit,
  editable,
  editSecured,
  companyName,
  address,
  timezoneId,
  referenceSlot,
  badgesSlot,
  contactsSlot,
  noteSlot,
  showDivider,
  dateSection,
  rampName,
  dataTestId,
}) => {
  return (
    <Card>
      <div className="flex flex-col gap-2" data-testid={dataTestId}>
        <EditableHeading3 title={title} icon={<SvgMapPin className="text-content-secondary" />} titleVariant={'h4'} onEditClick={onEdit} editable={editable} secured={editSecured} />

        {companyName && <div className={textLine}>{companyName}</div>}
        {address && <div className={textLine}>{address}</div>}
        {timezoneId && <COTimezone timezoneId={timezoneId} dataTestId={dataTestId ? `${dataTestId}-timezone` : undefined} />}
        {referenceSlot}
        {badgesSlot}
        {contactsSlot}

        {showDivider && <div className="h-px bg-gray-200 my-2" />}

        {dateSection && (
          <div className="flex flex-col gap-2">
            <EditableHeading3
              title={dateSection.label}
              icon={<SvgCalendar className="text-content-secondary" />}
              titleVariant={'h4'}
              onEditClick={dateSection.onEdit}
              editable={dateSection.editable}
              secured={dateSection.secured}
            />
            {dateSection.content ?? dateSection.emptyState}
            {rampName && <div className={textLine}>{rampName}</div>}
          </div>
        )}

        {noteSlot}
      </div>
    </Card>
  );
};

export default LocationDetailCard;
